import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { LoadingSpinner, PageLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { ausschreibungenListPath } from '../utils/ausschreibungRoutes';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNavigateBack } from '../hooks/useNavigateBack';
import { fetchMe, getCachedCapabilities } from '../lib/meClient';
import { TenderDropzone } from '../features/ausschreibungen/components/TenderDropzone';
import {
  TenderCustomerForm,
  type TenderCustomerFields,
} from '../features/ausschreibungen/components/TenderCustomerForm';
import { TenderReviewLeftRail } from '../features/ausschreibungen/components/TenderReviewLeftRail';
import { TenderLvPanel } from '../features/ausschreibungen/components/TenderLvPanel';
import { TenderLegacyDraftPanel } from '../features/ausschreibungen/components/TenderLegacyDraftPanel';
import { TenderQuotesPanel } from '../features/ausschreibungen/components/TenderQuotesPanel';
import { TenderPriceMatrix } from '../features/ausschreibungen/components/TenderPriceMatrix';
import {
  isTenderGaebDocument,
  type AusschreibungDetail,
  type SupplierQuote,
  type TenderDocument,
  type TenderRow,
} from '../features/ausschreibungen/types';
import { parseDDMMYYYYToYMD } from '../utils/date';

type FlowPhase = 'upload' | 'confirm' | 'akte';

function exStr(v: unknown): string {
  if (v == null || v === '') return '';
  return String(v).trim();
}

function buildCustomerFields(
  a: AusschreibungDetail | null,
  pending: Partial<TenderCustomerFields> | null,
): TenderCustomerFields {
  const p = a?.contact;
  const base: TenderCustomerFields = {
    title: '',
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    contact_address: '',
    contact_type: '',
    customer_street: '',
    customer_zip: '',
    customer_city: '',
    deadline: '',
    execution: '',
    binding_period_days: '',
  };
  if (!a) return { ...base, ...pending };
  return {
    title: exStr(a.title),
    first_name: p?.first_name ?? exStr(a.customer_first_name),
    last_name: p?.last_name ?? exStr(a.customer_last_name),
    company: (p?.company ?? exStr(a.customer_company)) || '',
    email: p?.email ?? '',
    phone: p?.phone ?? '',
    contact_address: p?.address ?? '',
    contact_type: p?.contact_type ?? '',
    customer_street: exStr(a.customer_street),
    customer_zip: exStr(a.customer_zip),
    customer_city: exStr(a.customer_city),
    deadline: exStr(a.deadline).slice(0, 10),
    execution: exStr(a.execution),
    binding_period_days: a.binding_period_days != null ? String(a.binding_period_days) : '',
    ...pending,
  };
}

function buildExtractedCustomerPatch(
  fields: Record<string, string | number | null>,
): Partial<TenderCustomerFields> {
  const patch: Partial<TenderCustomerFields> = { contact_type: 'Gewerblicher Kunde' };
  const put = (k: keyof TenderCustomerFields, v: string | number | null | undefined) => {
    if (v == null || v === '') return;
    patch[k] = typeof v === 'number' ? String(v) : String(v).trim();
  };
  put('title', fields.title as string | null);
  put('first_name', fields.first_name as string | null);
  put('last_name', fields.last_name as string | null);
  put('company', fields.customer_company as string | null);
  put('email', fields.email as string | null);
  put('phone', fields.phone as string | null);
  put('contact_address', fields.contact_address as string | null);
  put('customer_street', fields.customer_street as string | null);
  put('customer_zip', fields.customer_zip as string | null);
  put('customer_city', fields.customer_city as string | null);
  if (!patch.contact_address) {
    const composed = [
      fields.customer_street,
      [fields.customer_zip, fields.customer_city].filter(Boolean).join(' '),
    ]
      .map((p) => (p == null ? '' : String(p).trim()))
      .filter(Boolean)
      .join(', ');
    if (composed) patch.contact_address = composed;
  }
  const rawDeadline = fields.deadline as string | null;
  if (rawDeadline?.trim()) {
    const normalized =
      /^\d{4}-\d{2}-\d{2}$/.test(rawDeadline.trim())
        ? rawDeadline.trim()
        : parseDDMMYYYYToYMD(rawDeadline) ?? rawDeadline.trim();
    put('deadline', normalized);
  }
  put('execution', fields.execution as string | null);
  put('binding_period_days', fields.binding_period_days as number | null);
  return patch;
}

function contactPayloadFromForm(next: TenderCustomerFields): Record<string, unknown> | null {
  const first_name = next.first_name.trim() || null;
  const last_name = next.last_name.trim() || null;
  const company = next.company.trim() || null;
  const email = next.email.trim() || null;
  const phone = next.phone.trim() || null;
  const address = next.contact_address.trim() || null;
  const contact_type = next.contact_type.trim() || null;
  const has =
    first_name ||
    last_name ||
    company ||
    email ||
    phone ||
    address ||
    (contact_type && contact_type.length > 0);
  if (!has) return null;
  return {
    first_name,
    last_name,
    company,
    email,
    phone,
    address,
    contact_type,
  };
}

const computePhase = (a: AusschreibungDetail | null, docs: TenderDocument[]): FlowPhase => {
  if (!a) return 'upload';
  if (a.offering_id) return 'akte';
  const hasLegacyRows = (a.offer_draft?.rows?.length ?? 0) > 0;
  if (hasLegacyRows) return 'akte';
  if (docs.length === 0) return 'upload';
  const siteOk = !!(
    a.customer_street?.trim() &&
    a.customer_zip?.trim() &&
    a.customer_city?.trim()
  );
  return siteOk ? 'akte' : 'confirm';
};

export function AusschreibungDetailPage() {
  const navigate = useNavigate();
  const goBack = useNavigateBack(ausschreibungenListPath());
  const { id: rawId } = useParams<{ id: string }>();
  const [ausschreibung, setAusschreibung] = React.useState<AusschreibungDetail | null>(null);
  const [documents, setDocuments] = React.useState<TenderDocument[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<FlowPhase>('upload');
  const [extracting, setExtracting] = React.useState(false);
  const [pendingMeta, setPendingMeta] = React.useState<Partial<TenderCustomerFields> | null>(null);
  const [savingMeta, setSavingMeta] = React.useState(false);
  const [importingLv, setImportingLv] = React.useState(false);
  const [importingPdf, setImportingPdf] = React.useState(false);
  const [tenderContactId, setTenderContactId] = React.useState<string | null>(null);
  const [tenderNewContact, setTenderNewContact] = React.useState(false);
  const [contactSelectionError, setContactSelectionError] = React.useState('');
  const [capabilities, setCapabilities] = React.useState<Record<string, boolean> | null>(() =>
    getCachedCapabilities(),
  );

  React.useEffect(() => {
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled) return;
      setCapabilities((me?.capabilities as Record<string, boolean> | undefined) ?? {});
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const canEditProjects = capabilities?.['web.projekte.edit'] === true;

  const ausschreibungDocumentTitle = React.useMemo(() => {
    if (!ausschreibung || !rawId || String(ausschreibung.id) !== String(rawId)) return null;
    return (
      ausschreibung.title?.trim() ||
      ausschreibung.customer_company?.trim() ||
      ausschreibung.customer_name?.trim() ||
      null
    );
  }, [ausschreibung, rawId]);
  useDocumentTitle(ausschreibungDocumentTitle);

  const phaseSyncedForIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    phaseSyncedForIdRef.current = null;
  }, [rawId]);

  React.useEffect(() => {
    if (!ausschreibung) return;
    setPendingMeta(null);
    setTenderContactId(ausschreibung.contact_id ?? null);
    setTenderNewContact(false);
    setContactSelectionError('');
  }, [ausschreibung?.id]);

  const handleTenderContactModeChange = React.useCallback(
    (next: { selectedContactId: string | null; newContactChosen: boolean }) => {
      setContactSelectionError('');
      setTenderNewContact(next.newContactChosen);
      setTenderContactId(next.newContactChosen ? null : next.selectedContactId);
      if (next.newContactChosen) {
        setPendingMeta((prev) => ({
          ...prev,
          contact_type: 'Gewerblicher Kunde',
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          contact_address: '',
        }));
      } else if (next.selectedContactId) {
        setPendingMeta((prev) => ({
          ...prev,
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          contact_address: '',
          contact_type: '',
        }));
      }
    },
    [],
  );

  const legacyRows: TenderRow[] = ausschreibung?.offer_draft?.rows ?? [];

  const loadAusschreibung = React.useCallback(async (): Promise<AusschreibungDetail | null> => {
    if (!rawId) return null;
    const aRes = await fetch(`/api/ausschreibungen/${encodeURIComponent(rawId)}`, { credentials: 'include' });
    if (!aRes.ok) return null;
    return (await aRes.json()) as AusschreibungDetail;
  }, [rawId]);

  const fetchData = React.useCallback(async () => {
    if (!rawId) return;
    setLoading(true);
    try {
      const [aRes, dRes] = await Promise.all([
        fetch(`/api/ausschreibungen/${encodeURIComponent(rawId)}`, { credentials: 'include' }),
        fetch(`/api/ausschreibungen/${encodeURIComponent(rawId)}/documents`, {
          credentials: 'include',
        }),
      ]);
      if (!aRes.ok) throw new Error('Ausschreibung nicht gefunden.');
      const aData = (await aRes.json()) as AusschreibungDetail;
      const dData = dRes.ok ? ((await dRes.json()) as TenderDocument[]) : [];
      setAusschreibung(aData);
      setDocuments(dData);
      const syncKey = rawId;
      if (phaseSyncedForIdRef.current !== syncKey) {
        phaseSyncedForIdRef.current = syncKey;
        setPhase(computePhase(aData, dData));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [rawId]);

  React.useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /** Akte nach Änderungen am Vorgang neu laden (ohne Ladezustand der ganzen Seite). */
  const refreshAusschreibung = React.useCallback(async () => {
    const next = await loadAusschreibung();
    if (next) setAusschreibung(next);
  }, [loadAusschreibung]);

  const [quotes, setQuotes] = React.useState<SupplierQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = React.useState(false);
  const refreshQuotes = React.useCallback(async () => {
    if (!rawId) return;
    setQuotesLoading(true);
    try {
      const [qRes, dRes] = await Promise.all([
        fetch(`/api/ausschreibungen/${encodeURIComponent(rawId)}/quotes`, { credentials: 'include' }),
        fetch(`/api/ausschreibungen/${encodeURIComponent(rawId)}/documents`, { credentials: 'include' }),
      ]);
      if (qRes.ok) setQuotes((await qRes.json()) as SupplierQuote[]);
      if (dRes.ok) setDocuments((await dRes.json()) as TenderDocument[]);
    } finally {
      setQuotesLoading(false);
    }
  }, [rawId]);

  React.useEffect(() => {
    if (phase === 'akte') void refreshQuotes();
  }, [phase, refreshQuotes]);

  // Solange Angebote gelesen werden, alle 3 s nachladen.
  const quotesBusy = quotes.some((q) => q.status === 'pending' || q.status === 'extracting');
  React.useEffect(() => {
    if (!quotesBusy || phase !== 'akte') return;
    const t = window.setInterval(() => void refreshQuotes(), 3000);
    return () => window.clearInterval(t);
  }, [quotesBusy, phase, refreshQuotes]);

  const customerValues = React.useMemo(
    () => buildCustomerFields(ausschreibung, pendingMeta),
    [ausschreibung, pendingMeta],
  );

  const persistMeta = async (next: TenderCustomerFields): Promise<boolean> => {
    if (!ausschreibung) return false;
    setSavingMeta(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        title: next.title,
        customer_street: next.customer_street,
        customer_zip: next.customer_zip,
        customer_city: next.customer_city,
        deadline: next.deadline.trim() ? next.deadline : null,
        execution: next.execution,
        binding_period_days:
          next.binding_period_days.trim() === '' ? null : Number(next.binding_period_days),
      };
      if (tenderContactId && !tenderNewContact) {
        body.contact_id = tenderContactId;
      } else if (tenderNewContact) {
        const contact = contactPayloadFromForm(next);
        if (contact) body.contact = contact;
      }

      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const payload = (await res.json().catch(() => ({}))) as { error?: string } & Partial<AusschreibungDetail>;
      if (!res.ok) {
        setError(
          typeof payload.error === 'string' ? payload.error : 'Speichern fehlgeschlagen.',
        );
        return false;
      }
      if (payload.id) {
        const detail = payload as AusschreibungDetail;
        // PATCH liefert keine lv_summary: vorhandene Kennzahlen behalten.
        setAusschreibung((prev) => ({ ...detail, lv_summary: detail.lv_summary ?? prev?.lv_summary ?? null }));
        setTenderContactId(detail.contact_id ?? null);
        setTenderNewContact(false);
        setPendingMeta(null);
      }
      return true;
    } finally {
      setSavingMeta(false);
    }
  };

  const handleExtractCustomer = async () => {
    if (!ausschreibung) return;
    setExtracting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}/extract-customer`,
        { method: 'POST', credentials: 'include' },
      );
      const body = (await res.json().catch(() => ({}))) as
        | { ok: true; fields: Record<string, string | number | null>; ausschreibung?: AusschreibungDetail }
        | { error: string };
      if (!res.ok || !('ok' in body)) {
        throw new Error(('error' in body && body.error) || 'Extraktion fehlgeschlagen.');
      }
      const extractedPatch = buildExtractedCustomerPatch(body.fields);
      if (body.ausschreibung?.id) {
        const detail = body.ausschreibung;
        setAusschreibung((prev) => ({ ...detail, lv_summary: detail.lv_summary ?? prev?.lv_summary ?? null }));
        setTenderContactId(detail.contact_id ?? null);
        setTenderNewContact(!detail.contact_id);
      }
      setPendingMeta(extractedPatch);
      setContactSelectionError('');
      setPhase('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExtracting(false);
    }
  };

  const hasGaebDoc = documents.some(
    (d) => d.doc_kind === 'gaeb' || isTenderGaebDocument(d.filename, d.mime_type),
  );
  const hasPdfDoc = documents.some(
    (d) => d.doc_kind !== 'gaeb' && (/\.pdf$/i.test(d.filename) || d.mime_type === 'application/pdf'),
  );

  /**
   * LV aus dem ersten PDF der Akte lesen (Seitenspeicher + Regel-Parser auf dem Server) und als
   * Positionen in den Vorgang übernehmen. Projekt und Vorgang legt der Server bei Bedarf an.
   * false: kein PDF, nichts erkannt oder Ersetzen abgelehnt. Wirft bei Fehlern.
   */
  const importLvFromPdf = async (replace: boolean): Promise<boolean> => {
    if (!ausschreibung) return false;
    setImportingPdf(true);
    setError(null);
    try {
      const base = `/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}`;
      const sRes = await fetch(`${base}/import-lv-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const sBody = (await sRes.json().catch(() => ({}))) as {
        jobId?: string;
        offering_id?: string;
        skipped?: boolean;
        error?: string;
      };
      if (!sRes.ok) throw new Error(sBody.error || 'LV aus PDF konnte nicht gestartet werden.');
      if (sBody.skipped || !sBody.jobId) return false;

      let rows: Array<{ kind: string }> = [];
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const pRes = await fetch(`${base}/lv-pdf/${encodeURIComponent(sBody.jobId)}`, { credentials: 'include' });
        const pBody = (await pRes.json().catch(() => ({}))) as {
          status?: string;
          error?: string;
          rows?: Array<{ kind: string }>;
        };
        if (!pRes.ok) throw new Error(pBody.error || 'Auslesen fehlgeschlagen.');
        if (pBody.status === 'error') throw new Error(pBody.error || 'LV konnte nicht ausgelesen werden.');
        if (pBody.status === 'done') {
          rows = pBody.rows ?? [];
          break;
        }
      }
      const structural = rows.filter((r) => r.kind !== 'text');
      if (structural.length === 0) return false;

      const apply = async (withReplace: boolean) =>
        fetch(`${base}/import-lv-pdf/apply${withReplace ? '?replace=1' : ''}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: structural }),
        });
      let aRes = await apply(replace);
      let aBody = (await aRes.json().catch(() => ({}))) as {
        ok?: boolean;
        inserted?: number;
        error?: string;
        code?: string;
        position_count?: number;
      };
      if (aRes.status === 409 && aBody.code === 'positions_exist') {
        const confirmed = window.confirm(
          `Der Vorgang enthält bereits ${aBody.position_count ?? 0} Positionen. Durch das LV aus dem PDF ersetzen?`,
        );
        if (!confirmed) return false;
        aRes = await apply(true);
        aBody = (await aRes.json().catch(() => ({}))) as typeof aBody;
      }
      if (!aRes.ok || !aBody.ok) throw new Error(aBody.error || 'LV konnte nicht übernommen werden.');
      await refreshAusschreibung();
      return (aBody.inserted ?? 0) > 0;
    } finally {
      setImportingPdf(false);
    }
  };

  const handleReadLvFromPdf = async () => {
    try {
      const ok = await importLvFromPdf(false);
      if (!ok) setError('Im PDF wurden keine LV-Positionen erkannt.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Kunde bestätigen, dann (wenn möglich) das LV direkt in den Vorgang übernehmen. */
  const handleConfirmCustomer = async () => {
    if (!ausschreibung) return;
    const ok = await persistMeta(customerValues);
    if (!ok) return;

    if (hasGaebDoc && canEditProjects && !ausschreibung.offering_id) {
      setImportingLv(true);
      setError(null);
      try {
        const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}/import-lv`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; code?: string };
        if (!res.ok && body.code !== 'positions_exist') {
          setError(typeof body.error === 'string' ? body.error : 'LV-Import fehlgeschlagen.');
        }
        await refreshAusschreibung();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setImportingLv(false);
      }
    } else if (!hasGaebDoc && hasPdfDoc && canEditProjects && !ausschreibung.offering_id) {
      // Keine GAEB-Datei, aber ein PDF: das LV daraus lesen.
      try {
        await importLvFromPdf(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
    setPhase('akte');
  };

  const goPhase = (p: FlowPhase) => {
    setPhase(p);
  };

  if (loading) {
    return (
      <div className="h-screen flex bg-app-surface font-sans overflow-hidden">
        <AppSidebar active="ausschreibungen" />
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Ausschreibungen" />
          <section className="flex-1 min-h-0">
            <div className="flex h-full min-h-0 flex-col border-t border-app-border bg-app-bg">
              <PageLoadingState />
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (!ausschreibung) {
    return (
      <div className="h-screen flex bg-app-surface font-sans overflow-hidden">
        <AppSidebar active="ausschreibungen" />
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
          <AppTopBar title="Ausschreibungen" />
          <section className="flex-1 min-h-0">
            <div className="h-full bg-app-bg overflow-hidden flex border-t border-app-border">
              <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-app-bg">
                <div className="flex-1 grid place-items-center text-app-text-subtle text-sm">
                  <div className="text-center">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p>{error ?? 'Ausschreibung nicht gefunden.'}</p>
                    <button
                      onClick={() => navigate(ausschreibungenListPath())}
                      className="mt-3 inline-flex items-center gap-1 text-sm text-pipedrive-blue hover:underline"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Zurück zur Liste
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-app-surface font-sans overflow-hidden">
      <AppSidebar active="ausschreibungen" />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Ausschreibungen" />
        <section className="flex-1 min-h-0">
          <div className="h-full bg-app-bg overflow-hidden flex border-t border-app-border">
            <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-app-bg">
              {error && (
                <div className="mx-6 my-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 inline-flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-hidden">
                {phase === 'akte' ? (
                  <div className="h-full min-h-0 grid grid-cols-1 gap-0 md:grid-cols-[minmax(19rem,26rem)_1fr]">
                    <div className="bg-app-bg px-3 py-2 min-h-0 min-w-0 h-full max-h-full overflow-hidden md:min-h-0 flex flex-col">
                      <TenderReviewLeftRail
                        ausschreibung={ausschreibung}
                        documents={documents}
                        onUploaded={(d) => {
                          setDocuments((prev) => [d, ...prev]);
                          if (d.doc_kind === 'supplier_quote') void refreshQuotes();
                        }}
                        onDeleted={(id) => {
                          setDocuments((prev) => prev.filter((x) => x.id !== id));
                          setQuotes((prev) => prev.filter((q) => q.source_document_id !== id));
                        }}
                        customerEdit={{
                          values: customerValues,
                          onChange: (patch) =>
                            setPendingMeta((prev) => ({ ...prev, ...patch })),
                          selectedContactId: tenderContactId,
                          newContactChosen: tenderNewContact,
                          onContactModeChange: handleTenderContactModeChange,
                          contactPickError: contactSelectionError,
                          onPersist: () => persistMeta(customerValues),
                          onDiscardDraft: () => setPendingMeta(null),
                        }}
                      />
                    </div>
                    <div className="app-scrollbar min-h-0 min-w-0 h-full overflow-y-auto overscroll-contain px-3 py-2">
                      <div className="flex flex-col gap-3">
                        <TenderLvPanel
                          ausschreibung={ausschreibung}
                          documents={documents}
                          onChanged={refreshAusschreibung}
                          canEditProjects={canEditProjects}
                          onImportPdf={handleReadLvFromPdf}
                          importingPdf={importingPdf}
                          hasPdfDoc={hasPdfDoc}
                        />
                        <TenderQuotesPanel
                          ausschreibungId={ausschreibung.id}
                          quotes={quotes}
                          documents={documents}
                          loading={quotesLoading}
                          onRefresh={refreshQuotes}
                        />
                        <TenderPriceMatrix
                          ausschreibungId={ausschreibung.id}
                          hasLv={!!ausschreibung.offering_id}
                          quoteCount={quotes.filter((q) => q.status === 'ready').length}
                          canEditProjects={canEditProjects}
                          onPricesApplied={refreshAusschreibung}
                          onMatched={refreshQuotes}
                        />
                        <TenderLegacyDraftPanel rows={legacyRows} hasOffering={!!ausschreibung.offering_id} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="app-scrollbar h-full overflow-y-auto overscroll-contain p-4">
                    <div className="max-w-3xl mx-auto flex flex-col gap-4">
                      <PhaseSteps phase={phase} onSelect={goPhase} />

                      {phase === 'upload' && (
                        <section className="rounded-lg border border-app-border bg-app-surface p-5 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <h2 className="text-base font-semibold text-app-text">Ausschreibungs Dokumente</h2>
                            <span className="text-sm text-app-text-subtle">{documents.length} Dateien</span>
                          </div>
                          <TenderDropzone
                            ausschreibungId={ausschreibung.id}
                            documents={documents}
                            onUploaded={(d) => setDocuments((prev) => [d, ...prev])}
                            onDeleted={(id) =>
                              setDocuments((prev) => prev.filter((x) => x.id !== id))
                            }
                          />
                          <div className="mt-4 pt-4 border-t border-app-border flex flex-wrap items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setTenderContactId(null);
                                setTenderNewContact(true);
                                setPendingMeta((prev) => ({
                                  ...prev,
                                  contact_type: 'Gewerblicher Kunde',
                                  first_name: '',
                                  last_name: '',
                                  company: '',
                                  email: '',
                                  phone: '',
                                  contact_address: '',
                                }));
                                setPhase('confirm');
                              }}
                              className="text-sm text-app-text-muted hover:text-pipedrive-blue px-3 py-2 rounded-md transition"
                            >
                              Manuell weiter
                            </button>
                            {documents.length > 0 && (
                              <button
                                type="button"
                                onClick={() => void handleExtractCustomer()}
                                disabled={extracting || !hasGaebDoc}
                                className="inline-flex items-center gap-2 rounded-md bg-pipedrive-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                title={
                                  hasGaebDoc
                                    ? 'Kundendaten aus GAEB-Datei extrahieren'
                                    : 'Für die Extraktion wird eine GAEB-Datei benötigt. Sonst „Manuell weiter“ wählen.'
                                }
                              >
                                {extracting ? (
                                  <>
                                    <LoadingSpinner />
                                    Extrahiere…
                                  </>
                                ) : (
                                  'Kundendaten extrahieren'
                                )}
                              </button>
                            )}
                          </div>
                        </section>
                      )}

                      {phase === 'confirm' && (
                        <section className="rounded-lg border border-app-border bg-app-surface p-5 shadow-sm space-y-5">
                          <div>
                            <h2 className="text-base font-semibold text-app-text">Kunde bestätigen</h2>
                            <p className="text-sm text-app-text-subtle mt-1">
                              Kontaktdaten werden wie bei Projekten im Stammdaten-Kontakt gespeichert. Anschließend
                              werden Projekt und Vorgang angelegt und das LV aus der GAEB-Datei übernommen.
                            </p>
                          </div>
                          <TenderCustomerForm
                            values={customerValues}
                            onChange={(patch) =>
                              setPendingMeta((prev) => ({ ...prev, ...patch }))
                            }
                            selectedContactId={tenderContactId}
                            newContactChosen={tenderNewContact}
                            onContactModeChange={handleTenderContactModeChange}
                            contactPickError={contactSelectionError}
                          />
                          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-app-border">
                            {(savingMeta || importingLv || importingPdf) && (
                              <span className="text-sm text-app-text-subtle inline-flex items-center gap-1 mr-auto">
                                <LoadingSpinner size="xs" />
                                {importingPdf
                                  ? 'Lese Leistungsverzeichnis aus dem PDF…'
                                  : importingLv
                                    ? 'Lese LV in den Vorgang…'
                                    : 'Speichere…'}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => void handleConfirmCustomer()}
                              disabled={savingMeta || importingLv || importingPdf}
                              className="inline-flex items-center gap-2 rounded-md bg-pipedrive-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition"
                            >
                              Weiter zur Akte
                            </button>
                          </div>
                        </section>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const PhaseSteps: React.FC<{
  phase: FlowPhase;
  onSelect: (p: FlowPhase) => void;
}> = ({ phase, onSelect }) => {
  const currentId = phase === 'upload' ? 1 : phase === 'confirm' ? 2 : 3;
  const steps: { id: number; label: string; phase: FlowPhase }[] = [
    { id: 1, label: 'Hochladen', phase: 'upload' },
    { id: 2, label: 'Kunde bestätigen', phase: 'confirm' },
    { id: 3, label: 'Akte & LV', phase: 'akte' },
  ];
  return (
    <ol className="rounded-lg border border-app-border bg-app-surface px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
      {steps.map((s, i) => {
        const done = currentId > s.id;
        const active = currentId === s.id;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onSelect(s.phase)}
              className="flex items-center gap-2 rounded-md px-1 py-0.5 -mx-1 text-left transition hover:bg-app-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                  done
                    ? 'bg-emerald-500 text-white'
                    : active
                      ? 'bg-pipedrive-blue text-white'
                      : 'border border-app-border-strong text-slate-400'
                }`}
              >
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.id}
              </span>
              <span
                className={`text-sm ${
                  active
                    ? 'font-semibold text-app-text'
                    : done
                      ? 'text-app-text-muted'
                      : 'text-slate-400'
                }`}
              >
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && <span className="hidden sm:inline-block w-6 h-px bg-slate-200 shrink-0" />}
          </li>
        );
      })}
    </ol>
  );
};
